import { useCallback, useEffect, useState } from 'react';
import { Variant } from '@/src/types/availablePlans';
import { UF } from '@/src/types/uf';
import styles from './Quoter.module.scss';
import TextField from '@/designSystem/TextField';
import { formatPositiveIntegers, formatUF } from '@/utils/format';
import { variantsStepTexts } from '@/utils/generalTexts';

type VariantModelT = {
  totalWorkers: number;
  UF: UF;
  distribution: Variant[];
  setDistribution: (variant: Variant[]) => void;
  counterWorkers: number;
};

function Variants({ totalWorkers, distribution, setDistribution, counterWorkers }: VariantModelT) {
  const [totalUFVariants, setTotalUFVariants] = useState(0);
  const [usedWorkers, setUsedWorkers] = useState(counterWorkers);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, key: number, variant: Variant) => {
      const inputValue = formatPositiveIntegers(event.target.value);
      const prevWorkers = variant.input?.workers || 0;
      const prevUF = variant.input?.uf || 0;
      const newUF = parseFloat((variant.price.uf * inputValue).toFixed(2));

      const updatedUsedWorkers = usedWorkers - prevWorkers + inputValue;
      const isOverLimit = !!inputValue && updatedUsedWorkers > totalWorkers;
      const maxWorkers =
        totalWorkers - (usedWorkers - prevWorkers) <= 0
          ? totalWorkers
          : totalWorkers - (usedWorkers - prevWorkers);

      const updatedVariants = distribution.map((item, idx) =>
        idx === key
          ? {
              ...item,
              input: {
                ...item.input,
                workers: inputValue,
                uf: newUF,
                hasError: isOverLimit,
                feedback: isOverLimit
                  ? variantsStepTexts.distributionModal.feedback(maxWorkers)
                  : '',
              },
            }
          : item
      );

      setDistribution(updatedVariants);
      setUsedWorkers(updatedUsedWorkers);
      setTotalUFVariants(totalUFVariants - prevUF + newUF);
    },
    [distribution, setDistribution, totalWorkers, usedWorkers, totalUFVariants]
  );

  useEffect(() => {
    const totalUF = distribution.reduce((acum: number, variant: Variant) => {
      return acum + (variant.input?.uf || 0);
    }, 0);

    setTotalUFVariants(totalUF);
  }, []);

  const formatByUnit = (price: number) => formatUF(price);

  const setDetailInput = (variant: Variant) => `+ ${formatByUnit(variant.input?.uf || 0)}`;

  return (
    <div className={styles.quoter_container_content_variants_distribution}>
      {distribution.map((variant, key) => (
        <div key={`variant-${key}`}>
          {`Trabajadores ${variant.texts.distribution.summary.toLowerCase()}`}
          <div className={styles.quoter_container_content_variants_distribution_container}>
            <TextField
              id={`variant-input-${key}`}
              label="Nº de trabajadores"
              value={variant.input?.workers || ''}
              onChange={(e) => handleChange(e, key, variant)}
              hasError={variant.input?.hasError}
              feedback={variant.input?.feedback}
              className={styles.variants_textfield}
              {...{
                pattern: '[0-9]*',
                inputMode: 'numeric',
              }}
            />
            <div className={styles.quoter_container_content_variants_distribution_text}>
              <div>{setDetailInput(variant)}</div>
              <div>Adicional al plan base</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default Variants;
